import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Languages, Moon, PanelLeft } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { flatNavigation, canLeaveSectionFor } from './navigation.config';
import { usePermissionCheckers } from '@/hooks/usePermission';
import { useUiStore } from '@/stores/ui.store';
import { useSections, useActiveModules, useActiveSectionSlug } from '@/features/sections/sections.queries';
import { useAuth } from '@/features/auth/AuthContext';
import { SUPPORTED_LOCALES } from '@/lib/i18n';

/**
 * ⌘K / Ctrl-K palette (docs/04 §1).
 *
 * It is built from the same filtered navigation the sidebar renders, so it can never
 * offer a shortcut to a screen the user's role does not grant — a palette that lists
 * everything is a map of the parts of the app you are not allowed into.
 */
/**
 * Cycle to the next language rather than toggling between two.
 *
 * With three languages a toggle can only ever reach two of them — the palette was the one
 * place a keyboard user could switch, so a third locale silently made it a dead end.
 */
const nextLocale = (current) => {
  const codes = SUPPORTED_LOCALES.map((l) => l.code);
  return codes[(codes.indexOf(current) + 1) % codes.length];
};

export const CommandPalette = ({ open, onOpenChange }) => {
  const { t } = useTranslation(['common', 'nav']);
  const navigate = useNavigate();
  const checkers = usePermissionCheckers();

  const activeSectionId = useUiStore((state) => state.activeSectionId);
  const locale = useUiStore((state) => state.locale);
  const setLocale = useUiStore((state) => state.setLocale);
  const theme = useUiStore((state) => state.theme);
  const setTheme = useUiStore((state) => state.setTheme);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);

  /*
   * The palette is a second door onto the same menu, so it filters on the same two
   * things the sidebar does — otherwise ⌘K becomes the way around a disabled module.
   * `useSections` is still called (not for its rows) so the list is warm by the time the
   * dialog opens; `useActiveModules` reads from the same cache.
   */
  const { data: sections = [] } = useSections({ enabled: open });
  const modules = useActiveModules();
  const sectionSlug = useActiveSectionSlug();
  const { user } = useAuth();
  const canLeaveSection = canLeaveSectionFor(user?.access);

  /**
   * The same rule the sidebar follows: a vertical's own screen is opened *standing in*
   * that vertical, because the screen behind the row is shared and reads the switcher to
   * know whose books it writes. Duplicated as three lines rather than lifted into a hook —
   * a hook whose whole body is "look up a slug and set a store field" is indirection with
   * nothing in it, and the two callers would still each have to decide when to call it.
   */
  const setActiveSection = useUiStore((state) => state.setActiveSection);
  const queryClient = useQueryClient();

  const enterVertical = (item) => {
    const slug = item.groupSections?.length === 1 ? item.groupSections[0] : null;
    const target = slug ? sections.find((section) => section.slug === slug) : null;
    if (!target || target.id === activeSectionId) return;
    setActiveSection(target.id);
    queryClient.clear();
  };

  const items = React.useMemo(
    /* The same context the sidebar builds, so ⌘K and the menu never disagree about what
       exists — including the section narrowing (`canLeaveSection`, `isGroupVisible`). A
       palette that reaches a screen the menu has hidden is a second, invisible menu. */
    () => (open ? flatNavigation(checkers, { activeSectionId, modules, sectionSlug, canLeaveSection }) : []),
    [open, checkers, activeSectionId, modules, sectionSlug, canLeaveSection],
  );

  const run = (action) => {
    onOpenChange(false);
    action();
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder={t('common:commandPalette.placeholder')} />
      <CommandList>
        <CommandEmpty>{t('common:commandPalette.empty')}</CommandEmpty>

        <CommandGroup heading={t('common:commandPalette.navigation')}>
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                /*
                 * Keyed by group *and* item: a section vertical and a finance group can
                 * legitimately point at the same screen — Agency's Customers and Income's
                 * are one register read two ways — and keying on `to` alone made those a
                 * duplicate React key.
                 */
                key={`${item.groupKey}:${item.key}`}
                // Searching should match the group too: typing "treasury" finds Safes.
                value={`${t(`nav:groups.${item.groupKey}`)} ${t(`nav:items.${item.key}`)}`}
                onSelect={() =>
                  run(() => {
                    enterVertical(item);
                    navigate(item.to);
                  })
                }
              >
                <Icon />
                <span>{t(`nav:items.${item.key}`)}</span>
                <span className="ms-auto text-caption text-muted-foreground">
                  {t(`nav:groups.${item.groupKey}`)}
                </span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={t('common:commandPalette.preferences')}>
          <CommandItem
            value={t('common:commandPalette.toggleTheme')}
            onSelect={() => run(() => setTheme(theme === 'dark' ? 'light' : 'dark'))}
          >
            <Moon />
            {t('common:commandPalette.toggleTheme')}
          </CommandItem>
          <CommandItem
            value={t('common:commandPalette.toggleLanguage')}
            onSelect={() => run(() => setLocale(nextLocale(locale)))}
          >
            <Languages />
            {t('common:commandPalette.toggleLanguage')}
          </CommandItem>
          <CommandItem value={t('common:commandPalette.toggleSidebar')} onSelect={() => run(toggleSidebar)}>
            <PanelLeft />
            {t('common:commandPalette.toggleSidebar')}
            <CommandShortcut>⌘B</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

/** Owns the ⌘K / Ctrl-K and ⌘B shortcuts and hands `open` to the palette. */
export const useCommandPalette = () => {
  const [open, setOpen] = React.useState(false);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);

  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.metaKey || event.ctrlKey)) return;

      if (event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((prev) => !prev);
      } else if (event.key.toLowerCase() === 'b') {
        event.preventDefault();
        toggleSidebar();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [toggleSidebar]);

  return { open, setOpen };
};

export default CommandPalette;
